import type { AnyEnvelope, Envelope, RecordType } from '@wiztime/core';
import type { OpLogEntry, OpLogInput, OpLogQuery } from './oplog';

/** Names of the entries of the repository's key/value `meta` table. */
export const MetaKey = {
  DeviceId: 'deviceId',
  LastPushedHlc: 'lastPushedHlc',
  /** Random identity of this physical database; see `Repo.dbInstanceId`. */
  DbInstanceId: 'dbInstanceId',
  /** Data-format version of the stored data (data dictionary §10); absent = 1. */
  DataFormatVersion: 'dataFormatVersion',
  /** Lowest build data-format version that may write the stored data; absent = 1. */
  MinReaderFormat: 'minReaderFormat',
} as const;
export type MetaKey = (typeof MetaKey)[keyof typeof MetaKey];

export interface ListOptions {
  /** Include tombstones (default false). */
  readonly includeDeleted?: boolean;
}

export interface ListSinceOptions {
  /** Include the record whose hlc equals the given one (default false: strictly newer only). */
  readonly inclusive?: boolean;
}

/** Read operations. Every returned envelope is a fresh copy without storage-only fields. */
export interface RepoReader {
  get(id: string): Promise<AnyEnvelope | undefined>;
  getMany(ids: readonly string[]): Promise<Array<AnyEnvelope | undefined>>;
  listByType<T extends RecordType>(type: T, options?: ListOptions): Promise<Array<Envelope<T>>>;
  /** Records of `type` whose personal date is `date` (blocks, check-in logs, notes). */
  listByTypeAndDate<T extends RecordType>(
    type: T,
    date: string,
    options?: ListOptions,
  ): Promise<Array<Envelope<T>>>;
  /** Records of `type` with a personal date from `from` to `to`, inclusive. */
  listByTypeInDateRange<T extends RecordType>(
    type: T,
    from: string,
    to: string,
    options?: ListOptions,
  ): Promise<Array<Envelope<T>>>;
  /** Every record, tombstones included. */
  listAll(): Promise<AnyEnvelope[]>;
  /** Records whose hlc is greater than `hlc` (or equal to it with `inclusive`), oldest first: the sync outbox. */
  listSince(hlc: string, options?: ListSinceOptions): Promise<AnyEnvelope[]>;
  /** The largest hlc stored, or the largest one not above `notAfter` when given; null when none. */
  maxHlc(notAfter?: string): Promise<string | null>;
  getMeta(key: string): Promise<string | undefined>;
  /** Operation-log entries matching `query`, newest first (data dictionary §11). */
  listOpLog(query?: OpLogQuery): Promise<OpLogEntry[]>;
  /** Number of operation-log entries. */
  countOpLog(): Promise<number>;
}

export interface RepoWriter extends RepoReader {
  /** Insert or replace a record (the caller has validated it). */
  put(record: AnyEnvelope): Promise<void>;
  putMany(records: readonly AnyEnvelope[]): Promise<void>;
  setMeta(key: string, value: string): Promise<void>;
  /** Remove every record, tombstones included, and the operation log. The device-local `meta` entries stay. */
  clearAll(): Promise<void>;
  /** Append an operation-log entry in the current transaction. */
  appendOpLog(entry: OpLogInput): Promise<void>;
}

/**
 * Persistence boundary. The Dexie implementation lives in `dexie/`; a SQLite implementation can
 * replace it for the desktop client. All writes of one user command happen in one `transaction`.
 */
export interface Repo extends RepoReader {
  /**
   * Random identity of this physical database. It is created when the database is created (or on
   * first use in a database that predates it) and replaced by `clearAll`, so a database that was
   * emptied, deleted or replaced reports a different value. Sync compares it with the one stored
   * in its configuration to notice that its cursor refers to data this database no longer holds.
   */
  dbInstanceId(): Promise<string>;
  transaction<T>(fn: (tx: RepoWriter) => Promise<T>): Promise<T>;
  /** Copy every record into the backup store, replacing the previous backup (data dictionary §10). */
  backup(): Promise<number>;
  /** Delete operation-log entries older than `beforeIso`; returns how many. */
  pruneOpLog(beforeIso: string): Promise<number>;
}
